// BRIEF 119, §1c's PRE-STEP — THE MATTER'S CAST, RESOLVED ONCE.
//
// [APPROVED — Laurent, 2026-08-23] "A third copy is exactly what the extraction
// exists to prevent." §1c needs the cast to seed the meeting dialog from an
// event, and App holds every pool it would need — so writing it there would have
// made three.
//
// A CURE, NOT AN IMPROVEMENT (D.4 rule 3). Same nine families, same pools, same
// order, same `.filter(Boolean)`. Nothing is renamed, nothing is added, and the
// two hosts keep every difference they had — see below, because those
// differences are the whole reason this takes an ids object instead of a matter
// row.
//
// ── WHAT THE TWO HOSTS DO NOT AGREE ABOUT ──────────────────────────────────
//
// Measured before the move, and preserved exactly:
//
//   family   CaseView.tsx:989-1015       CollectiveCaseView.tsx:4120-4157
//   ──────   ─────────────────────       ────────────────────────────────
//   clients  `cs.clIds`                  `cs.clientLinks` → `lk.clId`
//   cpIds    `cs`                        `cs`
//   opIds    `cs`                        **`ccIds`**
//   ocIds    `cs`                        `cs`
//   ocuIds   `cs`                        **`ccIds`**
//   exIds    `cs`                        `cs`
//   meIds    `cs`                        `cs`
//   arIds    `cs`                        `cs`
//   op2Ids   `cs`                        **`ccIds`**
//
// Three families come off a DIFFERENT OBJECT on the collective side, and its
// clients come from a link table rather than an id list. A helper that took the
// matter row and reached for `matter.opIds` itself would silently resolve the
// wrong list on one of its two callers — so the caller passes the ids, which is
// the one thing it alone knows, and this owns the map-find-filter. The
// divergence becomes VISIBLE at the two call sites instead of buried in two
// copies of a loop.
//
// The `any`s here are the ones the two hosts already carried at these lines
// (`(id: any) => cl.find((c: any) => …)`); the ratchet counts written `any`, and
// this MOVES them rather than adding any — the two hosts shed exactly what this
// file gains.

/** Anything with an `id`; the pools are the app's own party arrays. */
export type CastPerson = { id?: number | null };

/** The nine id lists, each supplied by the caller from wherever IT keeps them. */
export type CastIds = {
  clIds?: readonly unknown[] | null;
  cpIds?: readonly unknown[] | null;
  opIds?: readonly unknown[] | null;
  ocIds?: readonly unknown[] | null;
  ocuIds?: readonly unknown[] | null;
  exIds?: readonly unknown[] | null;
  meIds?: readonly unknown[] | null;
  arIds?: readonly unknown[] | null;
  op2Ids?: readonly unknown[] | null;
};

/** The nine pools, in the app's own naming. */
export type CastPools<P extends CastPerson = CastPerson> = {
  clients: readonly P[];
  contactPersons: readonly P[];
  opponents: readonly P[];
  opponentContacts: readonly P[];
  opponentLawyers: readonly P[];
  experts: readonly P[];
  mediators: readonly P[];
  arbiters: readonly P[];
  additionalOpponents: readonly P[];
};

/** What both panes destructure. Field names are the hosts' own variable names. */
export type MatterCast<P extends CastPerson = CastPerson> = {
  cls: P[];
  cps: P[];
  ops: P[];
  ocs: P[];
  ols: P[];
  exs: P[];
  mes: P[];
  ars: P[];
  op2s: P[];
};

/** The one loop both panes wrote nine times each: resolve ids against a pool,
 *  in the id list's own order, dropping ids that resolve to nothing. */
function resolve<P extends CastPerson>(
  ids: readonly unknown[] | null | undefined,
  pool: readonly P[],
): P[] {
  return (Array.isArray(ids) ? ids : [])
    .map((id) => pool.find((p) => p && p.id === id))
    .filter(Boolean) as P[];
}

/** 216 §4 [RULED 71, 73] — an ORDER for every family, or none: the collective view hands ruling 71's
 *  (`orderParties`, src/lib/partyOrder.mjs) so each list it draws reads by last name; the regular view hands
 *  nothing and keeps the id list's own order (ruling 73 — the order the parties were added, stored by 215). */
/** The identity order — a typed VALUE witness (src/lib/fnTypes.ts's way: a function type literal's parameter
 *  names are counted unused by the base `no-unused-vars`), and what a caller that hands no order gets. */
export const castOrderWitness = <P extends CastPerson>(rows: readonly P[]): P[] => [...rows];
export type CastOrder = typeof castOrderWitness;

export function matterCast<P extends CastPerson>(
  ids: CastIds,
  pools: CastPools<P>,
  order?: CastOrder,
): MatterCast<P> {
  const o: CastOrder = order ?? castOrderWitness;
  return {
    cls: o(resolve(ids.clIds, pools.clients)),
    cps: o(resolve(ids.cpIds, pools.contactPersons)),
    ops: o(resolve(ids.opIds, pools.opponents)),
    ocs: o(resolve(ids.ocIds, pools.opponentContacts)),
    ols: o(resolve(ids.ocuIds, pools.opponentLawyers)),
    exs: o(resolve(ids.exIds, pools.experts)),
    mes: o(resolve(ids.meIds, pools.mediators)),
    ars: o(resolve(ids.arIds, pools.arbiters)),
    op2s: o(resolve(ids.op2Ids, pools.additionalOpponents)),
  };
}
